import React, { useEffect, useState } from "react";
import client, { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Key, Plus, Edit2, Trash2, Eye, EyeOff } from "lucide-react";

function VarModal({ variable, onSave, onClose }) {
  const [form, setForm] = useState({ key: variable?.key || "", value: "", description: variable?.description || "", is_secret: variable?.is_secret || false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!form.key || !form.value) { setError("Key and value required"); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch (e) { setError(formatApiError(e)); setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{variable ? "Edit" : "Add"} Variable</DialogTitle></DialogHeader>
        {error && <div className="p-2 bg-red-50 rounded text-red-700 text-sm">{error}</div>}
        <div className="space-y-4">
          <div>
            <Label className="text-xs text-gray-500">Key * (auto-uppercased)</Label>
            <Input className="mt-1 font-mono" value={form.key} onChange={e => setForm(f => ({ ...f, key: e.target.value.toUpperCase() }))} placeholder="BASE_URL" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Value * {variable?.is_secret ? "(leave blank to keep current)" : ""}</Label>
            <Input className="mt-1" type={form.is_secret ? "password" : "text"} value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} placeholder={variable?.is_secret ? "••••••" : "https://api.example.com"} />
          </div>
          <div><Label className="text-xs text-gray-500">Description</Label><Input className="mt-1" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div className="flex items-center gap-2"><Switch checked={form.is_secret} onCheckedChange={v => setForm(f => ({ ...f, is_secret: v }))} /><Label className="text-xs text-gray-600">Secret (value encrypted at rest, masked in UI)</Label></div>
        </div>
        <p className="text-xs text-gray-400 mt-2">Use as <code className="bg-gray-100 px-1 rounded">{"{{$VAR:KEY}}"}</code> in JSON templates and expressions.</p>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function VariablesStorePage() {
  const [vars, setVars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVar, setEditingVar] = useState(null);
  const [showValues, setShowValues] = useState({});

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const res = await client.get("/variables/").catch(() => ({ data: [] }));
    setVars(res.data);
    setLoading(false);
  };

  const save = async (form) => {
    if (editingVar) await client.put(`/variables/${editingVar._id}`, form);
    else await client.post("/variables/", form);
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete variable?")) return;
    await client.delete(`/variables/${id}`);
    load();
  };

  return (
    <div className="p-6 max-w-4xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1"><Key size={18} className="text-red-600" /><h1 className="text-2xl font-bold text-gray-900">Variables Store</h1></div>
          <p className="text-gray-500 text-sm">Global constants for base URLs, shared tokens. Use <code className="bg-gray-100 px-1 rounded font-mono">{"{{$VAR:KEY}}"}</code> in templates.</p>
        </div>
        <Button className="bg-[#006080] hover:bg-[#004d66] text-white" onClick={() => { setEditingVar(null); setShowModal(true); }}><Plus size={15} className="mr-2" /> Add Variable</Button>
      </div>
      {loading ? <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-12 bg-white border border-gray-200 rounded animate-pulse" />)}</div>
      : vars.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <Key size={36} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">No variables yet</p>
          <Button className="mt-4 bg-[#006080] text-white" onClick={() => setShowModal(true)}>Add first variable</Button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full data-table text-sm">
            <thead><tr><th className="px-4 py-3 text-left">Key</th><th className="px-4 py-3 text-left">Value</th><th className="px-4 py-3 text-left">Description</th><th className="px-4 py-3 text-left">Actions</th></tr></thead>
            <tbody>
              {vars.map(v => (
                <tr key={v._id}>
                  <td className="px-4 py-2.5 font-mono font-semibold text-[#006080]">{v.key}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      {v.is_secret ? (
                        showValues[v._id] ? <span className="font-mono text-xs">{v.value}</span> : <span className="text-gray-400">••••••••</span>
                      ) : <span className="font-mono text-xs truncate max-w-[200px]">{v.value}</span>}
                      {v.is_secret && (
                        <button onClick={() => setShowValues(s => ({ ...s, [v._id]: !s[v._id] }))}>
                          {showValues[v._id] ? <EyeOff size={12} className="text-gray-400" /> : <Eye size={12} className="text-gray-400" />}
                        </button>
                      )}
                      {v.is_secret && <Badge variant="secondary" className="text-xs">secret</Badge>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-gray-400 text-xs">{v.description}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => { setEditingVar(v); setShowModal(true); }}><Edit2 size={10} /></Button>
                      <Button size="sm" variant="outline" className="h-6 px-2 text-xs text-red-500 hover:bg-red-50" onClick={() => del(v._id)}><Trash2 size={10} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showModal && <VarModal variable={editingVar} onSave={save} onClose={() => { setShowModal(false); setEditingVar(null); }} />}
    </div>
  );
}
